import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios, { CancelTokenSource } from "axios";
import { useDebounce } from "use-debounce";
import { useTranslation } from "react-i18next";
import { useToast } from "../context/ToastContext";
import {
  DocumentMaterial,
  DynamicField,
  StatusFieldDocument,
} from "../types/Types";

export interface UseDocumentsManufacturingResult {
  document: DocumentMaterial | null;
  loadingDocument: boolean;
  docs: DocumentMaterial[];
  loadingList: boolean;
  errorList: string | null;
  page: number;
  totalPages: number;
  total: number;
  absoluteTotal: number;
  limit: number;
  search: string;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  reloadList: () => void;
  createDocument: (headerData: {
    item: string;
    actualBatch: number;
    actualBatchKg: number;
    actualWaste: number;
    actualWasteKg: number;
    implementationBatch: number;
    implementationBatchKg: number;
    implementationWaste: number;
    implementationWasteKg: number;
    serial: string;
    deadline: string;
    personInCharge?: string; // Optional - removed per NATASHA.md
    date?: string;
    status?: string;
    lockedIngredient?: {
      ingredientId: string;
      selectedSubRecipeIndex: number;
    };
  }) => Promise<void>;
  updateDocument: (headerData: {
    item?: string;
    actualBatch?: number;
    actualBatchKg?: number;
    actualWaste?: number;
    actualWasteKg?: number;
    implementationBatch?: number;
    implementationBatchKg?: number;
    implementationWaste?: number;
    implementationWasteKg?: number;
    serial?: string;
    deadline?: string;
    personInCharge?: string; // Optional - removed per NATASHA.md
    date?: string;
    status?: string;
    lockedIngredient?: {
      ingredientId: string;
      selectedSubRecipeIndex: number;
    };
  }) => Promise<DocumentMaterial | undefined>;
  code?: string;
  name?: string;
  category?: string;
  _id?: string;
  clearDocument: () => void;
  loadDocumentById: (documentId: string) => Promise<void>;
  selectedStatus: StatusFieldDocument | null;
  setSelectedStatus: (status: StatusFieldDocument | null) => void;
  statusFields: StatusFieldDocument[];
  updateDynamicFields: (docId?: string, fields?: DynamicField[], append?: boolean) => Promise<DocumentMaterial | undefined>;
  deleteDocument: (documentId: string) => Promise<boolean>;
  documentTypeWarehouse: string | null;
  createWorkOrderLineItem: (entry: Record<string, any>) => Promise<void>;
}

export const useDocumentsManufacturing = (): UseDocumentsManufacturingResult => {
  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  const location = useLocation();
  const params = useParams<{ code?: string }>();
  const { showToast } = useToast();
  const { t } = useTranslation();

  // State for document type metadata (fetched from API if not in location.state)
  const [documentTypeMetadata, setDocumentTypeMetadata] = useState<{
    _id?: string;
    code?: string;
    name?: string;
    category?: "work-order";
  }>({});

  // Try to get metadata from location.state first, otherwise use fetched metadata or URL params
  const locationState = (location.state || {}) as {
    _id?: string;
    code?: string;
    name?: string;
    category?: "work-order";
  };

  const _id = locationState._id || documentTypeMetadata._id;
  const code = locationState.code || documentTypeMetadata.code || params.code;
  const name = locationState.name || documentTypeMetadata.name;
  const category = locationState.category || documentTypeMetadata.category;

  // State for latest document
  const [document, setDocument] = useState<DocumentMaterial | null>(null);
  const [loadingDocument, setLoadingDocument] = useState(false);

  // State for document type warehouse
  const [documentTypeWarehouse, setDocumentTypeWarehouse] = useState<string | null>(null);

  // States for documents list
  const [docs, setDocs] = useState<DocumentMaterial[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Search state + debounce
  const [search, setSearchState] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);

  // Pagination totals
  const [total, setTotal] = useState(0);
  const [absoluteTotal, setAbsoluteTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Reload flag to force reload of list
  const [reloadFlag, setReloadFlag] = useState(0);

  // Status fields
  const [selectedStatus, setSelectedStatus] = useState<StatusFieldDocument | null>(null);
  const [statusFields, setStatusFields] = useState<StatusFieldDocument[]>([]);

  const reloadList = useCallback(() => setReloadFlag((f) => f + 1), []);
  const clearDocument = () => setDocument(null);

  // Same text (e.g. setSearch("") after picking a document) would not refetch — clearing the list
  // then left the search dialog and the ‹ › arrows empty until F5.
  const searchRef = useRef("");
  const setSearch = useCallback((value: string) => {
    if (value === searchRef.current) return;
    searchRef.current = value;
    setPage(1);
    setDocs([]);
    setLoadingList(false);
    setErrorList(null);
    setSearchState(value);
  }, []);

  // Load a specific document by ID
  const loadDocumentById = useCallback(async (documentId: string) => {
    setLoadingDocument(true);
    try {
      const token = localStorage.getItem("token") ?? "";
      const res = await axios.get<DocumentMaterial>(
        `${apiUrl}/api/documents-material/${documentId}`,
        { headers: { "x-auth-token": token } }
      );
      setDocument(res.data);
    } catch (err: any) {
      
      setDocument(null);
    } finally {
      setLoadingDocument(false);
    }
  }, []);

  // Cache for document lists
  const cache = useRef<Map<string, { docs: DocumentMaterial[]; total: number; totalPages: number }>>(new Map());

  // Cancel token for documents list fetch
  const cancelListSource = useRef<CancelTokenSource | null>(null);

  // Cancel token for latest document fetch
  const cancelLatestDocSource = useRef<CancelTokenSource | null>(null);

  // Cancel token for status fields fetch
  const cancelStatusSource = useRef<CancelTokenSource | null>(null);

  // Fetch latest document by code
  const fetchLatestDocumentByCode = useCallback(async () => {
    if (!code) return;
    setLoadingDocument(true);

    if (cancelLatestDocSource.current) {
      cancelLatestDocSource.current.cancel("Canceled due to new fetchLatestDocumentByCode call");
    }
    cancelLatestDocSource.current = axios.CancelToken.source();

    try {
      const token = localStorage.getItem("token") ?? "";
      const res = await axios.get<DocumentMaterial>(
        `${apiUrl}/api/documents-material/by-type/${code}`,
        { headers: { "x-auth-token": token }, cancelToken: cancelLatestDocSource.current.token }
      );
      setDocument(res.data);
    } catch (err: any) {
      if (!axios.isCancel(err)) {
        
        setDocument(null);
      }
    } finally {
      setLoadingDocument(false);
    }
  }, [code]);

  // Fetch status fields by document type code
  const fetchStatusFieldsByCode = useCallback(async () => {
    if (!code) return;

    if (cancelStatusSource.current) {
      cancelStatusSource.current.cancel("Canceled due to new fetchStatusFieldsByCode call");
    }
    cancelStatusSource.current = axios.CancelToken.source();

    try {
      const token = localStorage.getItem("token") ?? "";
      const res = await axios.get<StatusFieldDocument[]>(
        `${apiUrl}/api/documentsType/status/${code}`,
        { headers: { "x-auth-token": token }, cancelToken: cancelStatusSource.current.token }
      );

      setStatusFields(res.data);
      setSelectedStatus(res.data.length > 0 ? res.data[0] : null);
    } catch (err: any) {
      if (!axios.isCancel(err)) {
        
        setStatusFields([]);
        setSelectedStatus(null);
      }
    }
  }, [code]);

  // Fetch document type warehouse by code
  const fetchDocumentTypeWarehouse = useCallback(async () => {
    if (!code) return;

    try {
      const token = localStorage.getItem("token") ?? "";
      const res = await axios.get(
        `${apiUrl}/api/documentsType/by-code/${code}`,
        { headers: { "x-auth-token": token } }
      );

      if (res.data && res.data.warehouse) {
        const warehouseId = typeof res.data.warehouse === 'object'
          ? res.data.warehouse._id
          : res.data.warehouse;

        setDocumentTypeWarehouse(warehouseId);
      } else {

        setDocumentTypeWarehouse(null);
      }
    } catch (err: any) {

      setDocumentTypeWarehouse(null);
    }
  }, [code, apiUrl]);

  // Fetch document type metadata when opening in new tab (location.state is empty)
  useEffect(() => {
    const fetchDocumentTypeMetadata = async () => {
      const documentTypeCode = params.code;
      // Only fetch if we have a code from URL params but no location.state
      if (documentTypeCode && !locationState._id && !documentTypeMetadata._id) {
        try {
          const token = localStorage.getItem("token") ?? "";
          const res = await axios.get(
            `${apiUrl}/api/documentsType/by-code/${documentTypeCode}`,
            { headers: { "x-auth-token": token } }
          );
          setDocumentTypeMetadata({
            _id: res.data._id,
            code: res.data.code,
            name: res.data.name,
            category: res.data.category,
          });
        } catch (err: any) {
          
        }
      }
    };

    fetchDocumentTypeMetadata();
  }, [params.code, locationState._id, documentTypeMetadata._id, apiUrl]);

  // Fetch paginated document list with infinite scroll support
  const fetchDocumentsList = useCallback(async (resetDocs = false) => {
    if (!code) return;
    setLoadingList(true);
    setErrorList(null);

    if (cancelListSource.current) {
      cancelListSource.current.cancel("Canceled due to new fetchDocumentsList call");
    }
    cancelListSource.current = axios.CancelToken.source();

    // For search, always start from page 1
    const currentPage = resetDocs ? 1 : page;

    try {
      const token = localStorage.getItem("token") ?? "";
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("limit", limit.toString());
      if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());

      const res = await axios.get<{
        docs: DocumentMaterial[];
        total: number;
        absoluteTotal: number;
        totalPages: number;
      }>(
        `${apiUrl}/api/documents-material/by-type/${code}/all?${params.toString()}`,
        { headers: { "x-auth-token": token }, cancelToken: cancelListSource.current.token }
      );

      // For infinite scroll, append to existing docs; for search/reset, replace
      if (resetDocs || currentPage === 1) {
        setDocs(res.data.docs);
      } else {
        setDocs(prevDocs => [...prevDocs, ...res.data.docs]);
      }

      
      setTotal(res.data.total);
      setAbsoluteTotal(res.data.absoluteTotal || res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (err: any) {
      if (!axios.isCancel(err)) {
        setErrorList(err?.response?.data?.error || err.message || "Unknown error");
      }
    } finally {
      setLoadingList(false);
    }
  }, [code, page, limit, debouncedSearch]);

  // Create new document with work order fields — planned values now derived server-side from lockedIngredient
  const createDocument = useCallback(
    async (headerData: {
      item: string;
      actualBatch: number;
      actualBatchKg: number;
      actualWaste: number;
      actualWasteKg: number;
      implementationBatch: number;
      implementationBatchKg: number;
      implementationWaste: number;
      implementationWasteKg: number;
      serial: string;
      deadline: string;
      personInCharge?: string; // Optional - removed per NATASHA.md
      date?: string;
      status?: string;
      lockedIngredient?: {
        ingredientId: string;
        selectedSubRecipeIndex: number;
      };
    }) => {
      if (!_id || !code) {

        return;
      }

      try {
        const token = localStorage.getItem("token") ?? "";
        const data: any = {
          documentType: _id,
          date: headerData.date || new Date().toISOString().split("T")[0],
          purchase: "WO-" + Date.now(), // Work order reference
          status: headerData.status || selectedStatus?._id,
          item: headerData.item,
          // Actual values (manual entry)
          actualBatch: headerData.actualBatch,
          actualBatchKg: headerData.actualBatchKg,
          actualWaste: headerData.actualWaste,
          actualWasteKg: headerData.actualWasteKg,
          // Implementation values (manual entry)
          implementationBatch: headerData.implementationBatch,
          implementationBatchKg: headerData.implementationBatchKg,
          implementationWaste: headerData.implementationWaste,
          implementationWasteKg: headerData.implementationWasteKg,
          serial: headerData.serial,
          deadline: headerData.deadline,
          dynamicFields: [],
        };

        // Locked ingredient reference (sub-recipe selection for GMP compliance)
        if (headerData.lockedIngredient) {
          data.lockedIngredient = headerData.lockedIngredient;
        }

        // Only add personInCharge if provided (not used in work orders per NATASHA.md)
        if (headerData.personInCharge) {
          data.personInCharge = headerData.personInCharge;
        }

        await axios.post(`${apiUrl}/api/documents-material`, data, {
          headers: { "x-auth-token": token },
        });

        // Clear cache and reload after create
        cache.current.clear();
        setPage(1);
        setDocs([]);
        await fetchLatestDocumentByCode();
        reloadList();
        showToast(t("toasts.documentCreated"), "success");
      } catch (error: any) {
        showToast(error?.response?.data?.message || t("toasts.genericError"), "error");
        setErrorList("error");
      }
    },
    [_id, code, fetchLatestDocumentByCode, selectedStatus, showToast, t]
  );

  // Update document by ID - personInCharge removed per NATASHA.md; planned values derived server-side
  const updateDocument = useCallback(
    async (headerData: {
      item?: string;
      actualBatch?: number;
      actualBatchKg?: number;
      actualWaste?: number;
      actualWasteKg?: number;
      implementationBatch?: number;
      implementationBatchKg?: number;
      implementationWaste?: number;
      implementationWasteKg?: number;
      serial?: string;
      deadline?: string;
      personInCharge?: string; // Optional - removed per NATASHA.md
      date?: string;
      status?: string;
      lockedIngredient?: {
        ingredientId: string;
        selectedSubRecipeIndex: number;
      };
    }) => {
      if (!document?._id) {

        return;
      }

      try {
        const token = localStorage.getItem("token") ?? "";
        const data: any = {
          date: headerData.date || new Date().toISOString().split("T")[0],
          status: headerData.status || selectedStatus?._id,
        };

        // Only add fields if provided
        if (headerData.item) data.item = headerData.item;
        // Actual values
        if (headerData.actualBatch !== undefined) data.actualBatch = headerData.actualBatch;
        if (headerData.actualBatchKg !== undefined) data.actualBatchKg = headerData.actualBatchKg;
        if (headerData.actualWaste !== undefined) data.actualWaste = headerData.actualWaste;
        if (headerData.actualWasteKg !== undefined) data.actualWasteKg = headerData.actualWasteKg;
        // Only add fields if provided - Implementation values
        if (headerData.implementationBatch !== undefined) data.implementationBatch = headerData.implementationBatch;
        if (headerData.implementationBatchKg !== undefined) data.implementationBatchKg = headerData.implementationBatchKg;
        if (headerData.implementationWaste !== undefined) data.implementationWaste = headerData.implementationWaste;
        if (headerData.implementationWasteKg !== undefined) data.implementationWasteKg = headerData.implementationWasteKg;
        if (headerData.serial !== undefined) data.serial = headerData.serial;
        if (headerData.deadline !== undefined) data.deadline = headerData.deadline;
        if (headerData.personInCharge) data.personInCharge = headerData.personInCharge;
        // Locked ingredient reference — immutable after creation, but send if provided on update
        if (headerData.lockedIngredient) data.lockedIngredient = headerData.lockedIngredient;

        const res = await axios.put(
          `${apiUrl}/api/documents-material/${document._id}`,
          data,
          { headers: { "x-auth-token": token } }
        );

        // Update the current document with the response
        setDocument(res.data);

        // Clear cache and reload the list
        cache.current.clear();
        setPage(1);
        setDocs([]);
        reloadList();

        showToast(t("toasts.documentUpdated"), "success");
        return res.data;
      } catch (error: any) {
        showToast(error?.response?.data?.message || t("toasts.genericError"), "error");
        throw error;
      }
    },
    [document, selectedStatus, reloadList, showToast, t]
  );

  // Delete document by ID
  const deleteDocument = useCallback(
    async (documentId: string) => {
      try {
        const token = localStorage.getItem("token") ?? "";
        await axios.delete(`${apiUrl}/api/documents-material/${documentId}`, {
          headers: { "x-auth-token": token },
        });

        // Clear cache and reload the list
        cache.current.clear();
        setPage(1);
        setDocs([]);
        reloadList();

        // After deletion, automatically fetch the latest document
        await fetchLatestDocumentByCode();

        showToast(t("toasts.documentDeleted"), "success");
        return true;
      } catch (error: any) {
        showToast(error?.response?.data?.message || t("toasts.genericError"), "error");
        throw error;
      }
    },
    [document, reloadList, fetchLatestDocumentByCode, showToast, t]
  );

  const updateDynamicFields = useCallback(
    async (docId?: string, fields: DynamicField[] = [], append: boolean = false) => {
      const targetId = docId ?? document?._id;
      if (!targetId) {
        
        return;
      }

      try {
        const token = localStorage.getItem("token") ?? "";
        const payload = { dynamicFields: fields, append };

        const res = await axios.patch<DocumentMaterial>(
          `${apiUrl}/api/documents-material/${targetId}/dynamic-fields`,
          payload,
          { headers: { "x-auth-token": token } }
        );

        if (document && document._id === targetId) {
          setDocument(res.data);
        }

        cache.current.clear();
        reloadList();

        return res.data;
      } catch (err) {
        
        throw err;
      }
    },
    [document, reloadList]
  );

  // Create a single document-line-item-work-order (used for auto-populating nested table rows)
  const createWorkOrderLineItem = useCallback(
    async (entry: Record<string, any>) => {
      const token = localStorage.getItem("token") ?? "";
      await axios.post(`${apiUrl}/api/document-line-item-work-order`, entry, {
        headers: { "x-auth-token": token },
      });
    },
    [apiUrl]
  );

  // Effects to trigger fetches
  useEffect(() => {
    fetchLatestDocumentByCode();
  }, [fetchLatestDocumentByCode]);

  useEffect(() => {
    fetchStatusFieldsByCode();
  }, [fetchStatusFieldsByCode]);

  useEffect(() => {
    fetchDocumentTypeWarehouse();
  }, [fetchDocumentTypeWarehouse]);

  // Reset to page 1 and clear docs when search changes
  useEffect(() => {
    setPage(1);
    setDocs([]);
  }, [debouncedSearch]);

  useEffect(() => {
    if (page === 1) {
      fetchDocumentsList(true); // Reset docs for first page
    } else {
      fetchDocumentsList(false); // Append for subsequent pages
    }
  }, [fetchDocumentsList, page, debouncedSearch, reloadFlag]);

  return {
    document,
    loadingDocument,
    docs,
    loadingList,
    errorList,
    page,
    totalPages,
    total,
    absoluteTotal,
    limit,
    search,
    setPage,
    setSearch,
    reloadList,
    createDocument,
    updateDocument,
    code,
    name,
    category,
    _id,
    clearDocument,
    loadDocumentById,
    selectedStatus,
    setSelectedStatus,
    statusFields,
    updateDynamicFields,
    deleteDocument,
    documentTypeWarehouse,
    createWorkOrderLineItem
  };
};
